import { storeToRefs } from 'pinia';
import { until } from '@vueuse/core';
import type { Router, RouteRecordName } from 'vue-router';
import { useAppStore } from 'src/core/stores/app';
import { useUserStore } from 'src/core/stores/user';

// list of routes that do not require authentication
const noAuthWhitelist: RouteRecordName[] = [
  'login',
  'userInviteVerify',
  'forgotPassword',
  'resetPassword',
  'ssoRedirect',
];

/**
 * A router guard function that checks if the user is authenticated before navigating to a route.
 * @param router - The Vue Router instance.
 */
export default function useAuthGuard(router: Router) {
  router.beforeEach(async (to: any, from: any, next) => {
    const { checkSigned } = storeToRefs(useUserStore());
    const { initialized, unAuthorizedlist, errorDetails, loading, t } =
      storeToRefs(useAppStore());

    await until(initialized)
      .toBe(true, {
        timeout: 30000,
        throwOnTimeout: true,
      })
      .catch(() => {
        loading.value = false;
        errorDetails.value = {
          message: t.value('app.request_tracking.errors.failed_to_initialize'),
          type: 'INITIALIZATION_ERROR',
        };
      });

    // store the last route
    to.meta.lastRoute = from;

    try {
      // wait for signIn status which is assigned asynchronously
      if (checkSigned.value) {
        if (['login'].includes(to.name)) {
          next({ name: 'datasets' });
        } else {
          if (!unAuthorizedlist.value.includes(to.name)) {
            next();
          } else {
            next({ name: 'noPermissions' });
          }
        }
      } else {
        throw new Error('not-logged-iUser is not logged in');
      }
    } catch (error) {
      if (noAuthWhitelist.includes(to.name)) {
        next();
      } else {
        next({ name: 'login' });
      }
    }
  });
}
